<template>
  <div>
    <v-dialog v-model="addJobDialog" max-width="500px" persistent>
      <v-card class="pa-4">
        <v-card-title>
          <h2 class="title">Set Up Custom Job Client, Tech and Date</h2>
        </v-card-title>
        <v-card-text>
          <div class="dialog-heading mb-4 mt-2">
            Add Job
          </div>
          <v-flex class="px-2">
            <v-flex>
              <v-autocomplete
                :items="clients"
                v-model="addJobData.client_id"
                label="Custom Job Client*"
                item-text="full_name"
                item-value="id"
                placeholder="Please Select"
                :required="true"
                :loading="loading"
                :error-messages="errors($v.addJobData.client_id) || server_errors.client_id"
                @focus="resetServerValidation"
                @blur="$v.addJobData.client_id.$touch()"
              ></v-autocomplete>
            </v-flex>
            <div v-if="loadingClientsSubscriptions" class="my-4 text-center">
              <v-progress-linear
                indeterminate
                style="height: 2px;"
              ></v-progress-linear>
              <div class="mt-2">Please wait...</div>
            </div>
            <v-flex v-if="!loadingClientsSubscriptions && subscriptions.length > 1">
              <v-autocomplete
                :items="subscriptions"
                v-model="addJobData.subscription_id"
                label="Subscription"
                item-text="name"
                item-value="id"
                placeholder="Please Select"
                :loading="loading"
                :error-messages="errors($v.addJobData.subscription_id) || server_errors.subscription_id"
                @focus="resetServerValidation"
                @blur="$v.addJobData.subscription_id.$touch()"
              ></v-autocomplete>
            </v-flex>
            <v-flex>
              <v-autocomplete
                :items="staff"
                v-model="addJobData.assigned_to_id"
                label="Custom Job Tech*"
                item-text="name"
                item-value="id"
                placeholder="Please Select"
                :required="true"
                :error-messages="errors($v.addJobData.assigned_to_id) || server_errors.assigned_to_id"
                @focus="resetServerValidation"
                @blur="$v.addJobData.assigned_to_id.$touch()"
              ></v-autocomplete>
            </v-flex>
            <v-menu
              ref="menu4"
              :label="`Custom Job Date`"
              :close-on-content-click="false"
              v-model="menu4"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-on="on"
                  v-model="addJobData.date"
                  :label="`Custom Job Date*`"
                  placeholder="Please Select"
                  prepend-icon="event"
                  readonly
                  :required="true"
                  :error-messages="errors($v.addJobData.date) || server_errors.date"
                  @focus="resetServerValidation"
                  @change="$v.addJobData.date.$touch()"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="addJobData.date"
                no-title
                :min="minDate"
                @input="menu4 = false"
              >
              </v-date-picker>
            </v-menu>
            <v-flex>
              <v-text-field
                label="Estimated Time"
                suffix="min"
                v-model="addJobData.estimate_time"
                placeholder="Please Enter"
                :error-messages="errors($v.addJobData.estimate_time)"
                @blur="$v.addJobData.estimate_time.$touch()"
              ></v-text-field>
            </v-flex>
            <v-flex v-if="canReoptimize">
              <v-checkbox
                label="Reoptimize Route"
                v-model="reoptimizeAfterAdding"
              ></v-checkbox>
            </v-flex>
          </v-flex>
          <v-alert
            class="pa-2 mt-4"
            :value="$v.addJobData.$error"
            outlined
            color="error"
            icon="priority_high"
            transition="scale-transition"
          >
            <span class="caption"
              >Please fill out all fields correctly</span
            >
          </v-alert>
          <v-alert
            class="pa-2 mt-4"
            :value="!serverValidation"
            outlined
            color="error"
            icon="error"
            transition="scale-transition"
          >
            <span class="caption">{{ serverValidationMessage }}</span>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeAddJobModal"
            >Cancel</v-btn
          >
          <v-btn
            color="primary"
            :loading="addJobLoading"
            :disabled="addJobLoading || loadingClientsSubscriptions"
            @click="addJob"
            >Add Job</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showInfoDialog" max-width="500px" persistent>
      <v-card class="pa-4">
        <v-card-title>
          <h2 class="title">
            Job was successfully created.
          </h2>
        </v-card-title>
        <v-card-text>
          <div class="px-1">
            <v-alert dense outlined color="error" icon="warning" class="mt-2 mb-2">
              <div class="body-2">
                Route was not reoptimized. Please retry via the route manager.
              </div>
            </v-alert>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="cloeseInfoModal"
            >Ok</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ApiService from "@/common/api.service";
import { validationMixin } from "vuelidate";
import { required, decimal, helpers } from "vuelidate/lib/validators";
import { valMessages, getValMessage } from "@/common/error-messages";
import { mapActions } from 'vuex'

let validations = {}

export default {
  name: 'AddJob',
  mixins: [validationMixin],
  validations() {
    validations = {
      addJobData: {
        client_id: { required },
        assigned_to_id: { required },
        date: { required },
        estimate_time: {
          decimal,
          positive: v => !helpers.req(v) || v > 0
        }
      }
    };
    if (
      !this.loadingClientsSubscriptions &&
      !this.no_data.includes(this.subscriptions) && 
      Array.isArray(this.subscriptions) &&
      this.subscriptions.length > 1
    ) {
      validations.addJobData.subscription_id = { required }
    }
    this.errors = getValMessage(valMessages)(validations)
    return validations
  },
  props: ['staff'],
  data: () => ({
    no_data: [null, "", undefined, "null"],
    loading: false,
    clients: [],
    menu4: false,
    addJobData: {},
    addJobDialog: false,
    addJobLoading: false,
    loadingClientsSubscriptions: false,
    subscriptions: [],
    daysInAdvance: 0,
    reoptimizeAfterAdding: false,
    showInfoDialog: false,
    serverValidation: true,
    serverValidationMessage: "Oops something went wrong...",
    server_errors: {},
    unsubscribe: null
  }),
  mounted() {
    this.unsubscribe = this.$store.subscribeAction(action => {
      if (action.type === 'vEmit' && action.payload.name === 'AddJob') {
          this.addJobModal(action.payload.data)
      }
    })
  },
  beforeDestroy() {
    this.unsubscribe()
  },
  methods: {
    ...mapActions(['vEmit']),
    getClients() {
      this.loading = true
      ApiService.get("/client/list", { statuses: ['active']})
        .then(response => {
          let data = response.data;
          this.clients = data.clients;
          this.loading = false
        })
        .catch(erorr => {
          console.log(error);
          this.loading = false
        });
    },
    getOrganizationSettings() {
      return new Promise((resolve, reject) => {
        ApiService.get('/scheduler/organization_settings')
          .then(response => {
            let organization_settings = {}
            response.data.organization_settings.forEach(i => {
              organization_settings[i.key] = i.value
            })
            organization_settings = {...organization_settings}
            this.daysInAdvance = organization_settings.days_in_advance || 0
            resolve()
          })
          .catch(error => {
            reject(error);
          });
      })
    },
    getClientsSubscriptions (id) {
      return new Promise((resolve, reject) => {
        this.loadingClientsSubscriptions = true
        ApiService.get("/subscription/list_active", { client_id: id })
          .then(response => {
            let data = response.data
            if (
              !this.no_data.includes(data) &&
              !this.no_data.includes(data.count_subscriptions) &&
              Array.isArray(data.count_subscriptions) &&
              data.count_subscriptions.length
            ) {
              const subscriptions = data.count_subscriptions.map(i => {
                let name = i.name 
                if (
                  !this.no_data.includes(i.pricing_plan) &&
                  !this.no_data.includes(i.service_plan)
                ) {
                  name = `${i.pricing_plan} - ${i.service_plan}`
                }
                return { ...i, name }
              })
              this.subscriptions = _.orderBy(subscriptions, ['name'],['asc'])
            } else {
              this.subscriptions = []
            }
            this.loadingClientsSubscriptions = false
            resolve(data)
          })
          .catch(({ response }) => {
            this.loadingClientsSubscriptions = false
            this.serverValidation = false
            if (response.data.error) {
              this.serverValidationMessage = this.titleCase(
                response.data.error
              );
            }
            reject()
          })
      })
    },
    addJobModal() {
      this.$v.$reset();
      this.resetServerValidation();
      this.addJobDialog = true;
      this.addJobData = {
        date: moment(new Date()).format("YYYY[-]MM[-]DD")
      }
      this.subscriptions = []
      this.reoptimizeAfterAdding = false
      this.getClients()
      this.getOrganizationSettings()
    },
    addJob() {
      this.resetServerValidation();
      this.$v.$touch();
      if (!this.$v.addJobData.$invalid) {
        this.staff.forEach(i => {
          if (i.id === this.addJobData.assigned_to_id) {
            this.addJobData.assigned_to_name = i.name;
          }
        });

        let data = {
          client_id: this.addJobData.client_id,
          date: this.addJobData.date,
          type: "custom",
          assigned_to_id: this.addJobData.assigned_to_id,
          assigned_to_name: this.addJobData.assigned_to_name,
          estimate_time: this.addJobData.estimate_time,
        };
        if (
          !this.no_data.includes(this.addJobData) &&
          !this.no_data.includes(this.addJobData.subscription_id)
        ) {
          data.subscription_id = this.addJobData.subscription_id
        } else if (this.subscriptions.length === 1) {
          data.subscription_id = this.subscriptions[0].id
        }
        this.addJobLoading = true;

        ApiService.put("/scheduler/job_create", data)
          .then(res => {
            if (this.canReoptimize && this.reoptimizeAfterAdding) {
              this.reoptimizeRoute()
                .then(() => {
                  this.completeJobAddition()
                })
                .catch(error => {
                  this.completeJobAddition(false)
                  this.showInfoDialog = true
                })
            } else {
              this.completeJobAddition()
            }
          })
          .catch(({ response }) => {
            this.addJobLoading = false;
            this.serverValidation = false;
            if (response.data.error) {
              this.serverValidationMessage = this.titleCase(
                response.data.error
              );
            }
            if (response.data.message) {
              this.serverValidationMessage = response.data.message;
            }
            if (response.data.errors) {
              this.server_errors = response.data.errors;
            }
          });
      }
    },
    completeJobAddition (showNotification = true) {
      this.addJobLoading = false;
      if (showNotification) {
        this.vEmit({ 
          name: "notification", 
          data: {
            status: "success",
            text: "Job has been successfully added."
          }
        })
      }
      this.vEmit({ name: "CheckPlanStatus", data: true });
      this.closeAddJobModal();
      this.$emit('done')
    },
    closeAddJobModal() {
      this.addJobDialog = false;
    },
    reoptimizeRoute () {
      return new Promise((resolve, reject) => {
        let body = {
          assigned_to_id: this.addJobData.assigned_to_id,
          date: this.addJobData.date
        }
        ApiService.get("/scheduler/reoptimize_route", body)
        .then(response => {
          resolve(response)
        })
        .catch(error => {
          reject(error)
        });
      })
    },
    cloeseInfoModal () {
      this.showInfoDialog = false
    },
    titleCase(str = "") {
      return _.startCase(str.replace(/_/g, " "));
    },
    resetServerValidation() {
      this.serverValidation = true;
      this.serverValidationMessage = "Oops something went wrong...";
      this.server_errors = {};
    },
    errors: getValMessage(valMessages)(validations)
  },
  computed: {
    minDate: function() {
      return moment().format("YYYY[-]MM[-]DD");
    },
    canReoptimize() {
      return !this.no_data.includes(this.addJobData) &&
        !this.no_data.includes(this.addJobData.date) &&
        !moment(this.addJobData.date).isAfter(moment().add(Number(this.daysInAdvance), 'days'))
    },
  },
  watch: {
    'addJobData.client_id': function(val) {
      if (!this.no_data.includes(this.addJobData)) {
        delete this.addJobData.subscription_id
      }
      this.$v.$reset()
      if(!this.no_data.includes(val)) {
        this.getClientsSubscriptions(val)
      }
    }
  }
}
</script>